import { type CSSProperties, type ReactNode } from 'react'
import { NodeResizer } from '@xyflow/react'
import { NODE_BASE_CLASS, NODE_DASHED_CLASS, NODE_MIN } from './constants'
import { cn } from '@/lib/utils'
import { useUiFlags } from '@/contexts/UiFlagsContext'

interface NodeContainerProps {
  variant?: 'default' | 'dashed' | 'parent'
  resizable?: boolean
  kind?: 'default' | 'subflow'
  children: ReactNode
  className?: string
  selected?: boolean
  sourceFile?: string
  zIndexClass?: string
  /** Custom inline styles (e.g., CSS variables for opacity) */
  style?: CSSProperties
}

export type { NodeContainerProps }

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w'

export default function NodeContainer({
  variant = 'default',
  resizable = true,
  kind = 'default',
  children,
  className,
  selected = false,
  sourceFile,
  zIndexClass,
  style,
}: NodeContainerProps) {
  const { showTrace } = useUiFlags()
  const base = variant === 'parent'
    ? 'parent-node'
    : (variant === 'dashed' ? NODE_DASHED_CLASS : NODE_BASE_CLASS)
  const min = kind === 'subflow' ? NODE_MIN.subflow : NODE_MIN.default
  const cls = cn(
    base,
    variant === 'parent' && selected && 'is-selected',
  )

  const renderResizeHandle = (handle: ResizeHandle) => {
    const positions: Record<ResizeHandle, string> = {
      nw: 'top-0 left-0 -translate-x-1/2 -translate-y-1/2',
      ne: 'top-0 right-0 translate-x-1/2 -translate-y-1/2',
      sw: 'bottom-0 left-0 -translate-x-1/2 translate-y-1/2',
      se: 'bottom-0 right-0 translate-x-1/2 translate-y-1/2',
      n: 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2',
      s: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2',
      e: 'top-1/2 right-0 translate-x-1/2 -translate-y-1/2',
      w: 'top-1/2 left-0 -translate-x-1/2 -translate-y-1/2',
    }

    const cursors: Record<ResizeHandle, string> = {
      nw: 'nw-resize',
      ne: 'ne-resize',
      sw: 'sw-resize',
      se: 'se-resize',
      n: 'n-resize',
      s: 's-resize',
      e: 'e-resize',
      w: 'w-resize',
    }

    return (
      <div
        key={handle}
        className={cn(
          'absolute w-3 h-3 bg-blue-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none',
          positions[handle]
        )}
        style={{ cursor: cursors[handle] }}
      />
    )
  }

  if (variant === 'parent') {
    // Estructura especial: artefacto + footer por fuera (debajo)
    const FOOTER_H = 28
    return (
      <div className={cn('group relative h-full', zIndexClass, className)} style={style}>
        <div className={cls} style={{ height: `calc(100% - ${FOOTER_H}px)`, width: '100%' }}>
          {resizable && <NodeResizer minWidth={min.width} minHeight={min.height} />}

          {/* Manual resize handles - sólo para subflow seleccionado */}
          {resizable && selected && kind === 'subflow' && (
            <>
              {renderResizeHandle('nw')}
              {renderResizeHandle('ne')}
              {renderResizeHandle('sw')}
              {renderResizeHandle('se')}
              {renderResizeHandle('n')}
              {renderResizeHandle('s')}
              {renderResizeHandle('e')}
              {renderResizeHandle('w')}
            </>
          )}

          {children}
        </div>

        {showTrace && sourceFile && (
          <div className="absolute right-2 bottom-1 z-10" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex flex-col items-end gap-0.5">
              <span className="text-[10px] text-muted-foreground bg-muted/60 border border-border rounded px-2 py-[2px] leading-none truncate max-w-[280px]">
                <span className="opacity-80 mr-1">Source:</span>
                {sourceFile}
              </span>
              <span className="text-[10px] text-muted-foreground">z-index: {zIndexClass ?? 'auto'}</span>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Caso general: artefacto + footer dentro del mismo contenedor (como antes)
  return (
    <div className={cn(cls, 'group relative', zIndexClass, className)} style={style}>
      {resizable && <NodeResizer minWidth={min.width} minHeight={min.height} />}

      {resizable && selected && kind === 'subflow' && (
        <>
          {renderResizeHandle('nw')}
          {renderResizeHandle('ne')}
          {renderResizeHandle('sw')}
          {renderResizeHandle('se')}
          {renderResizeHandle('n')}
          {renderResizeHandle('s')}
          {renderResizeHandle('e')}
          {renderResizeHandle('w')}
        </>
      )}

      {children}

      {showTrace && sourceFile && (
        <div className="mt-1 pt-1 border-t border-border relative z-10" onMouseDown={(e) => e.stopPropagation()}>
          <div className="w-full flex flex-col items-end gap-0.5">
            <span className="text-[10px] text-muted-foreground bg-muted/60 border border-border rounded px-2 py-[2px] leading-none truncate max-w-full">
              <span className="opacity-80 mr-1">Source:</span>
              {sourceFile}
            </span>
            <span className="text-[10px] text-muted-foreground">z-index: {zIndexClass ?? 'auto'}</span>
          </div>
        </div>
      )}
    </div>
  )
}
